import { useDownloadProgress } from '../contexts/DownloadProgressContext';

export interface WeldParameterDownloadData {
  deviceId: string;
  deviceName: string;
  status: 'initiated' | 'in_progress' | 'completed' | 'failed';
  message: string;
  progress?: number;
  currentChunk?: number;
  totalChunks?: number;
  error?: string;
}

export class WeldParameterDownloadService {
  private static instance: WeldParameterDownloadService;
  private downloadProgressContext: any = null;

  static getInstance(): WeldParameterDownloadService {
    if (!WeldParameterDownloadService.instance) {
      WeldParameterDownloadService.instance = new WeldParameterDownloadService();
    }
    return WeldParameterDownloadService.instance;
  }

  setContext(context: any) {
    this.downloadProgressContext = context;
  }

  startDownload(deviceId: string, deviceName: string): string {
    if (!this.downloadProgressContext) {
      throw new Error('Download progress context not set');
    }

    const downloadId = this.downloadProgressContext.addDownload(deviceId, deviceName);
    
    // Update with initial status
    this.updateDownloadStatus(downloadId, {
      deviceId,
      deviceName,
      status: 'initiated',
      message: 'Weld Parameter Download is initiated, please check back later'
    });

    return downloadId;
  }

  updateDownloadStatus(downloadId: string, data: WeldParameterDownloadData) {
    if (!this.downloadProgressContext) {
      return;
    }

    const updates: any = {
      status: data.status,
      currentFile: data.message
    };

    // Parse progress from chunk information
    if (data.currentChunk && data.totalChunks) {
      updates.progress = Math.round((data.currentChunk / data.totalChunks) * 100);
      updates.totalFiles = data.totalChunks;
      updates.downloadedFiles = data.currentChunk;
    } else if (data.progress !== undefined) {
      updates.progress = data.progress;
    }

    if (data.error) {
      updates.error = data.error;
    }

    this.downloadProgressContext.updateProgress(downloadId, updates);
  }

  completeDownload(downloadId: string) {
    if (!this.downloadProgressContext) {
      return;
    }

    this.downloadProgressContext.completeDownload(downloadId);
  }

  failDownload(downloadId: string, error: string) {
    if (!this.downloadProgressContext) {
      return;
    }

    this.downloadProgressContext.failDownload(downloadId, error);
  }

  parseWeldParameterMessage(message: string): WeldParameterDownloadData | null {
    // Check if it's a success message
    if (message === 'Weld Parameter has been downloaded successfully') {
      return {
        deviceId: '',
        deviceName: '',
        status: 'completed',
        message: message
      };
    }

    // Check if it's an initiated message
    if (message === 'Weld Parameter Download is initiated, please check back later') {
      return {
        deviceId: '',
        deviceName: '',
        status: 'initiated',
        message: message
      };
    }

    // Check for chunk progress message
    const chunkMatch = message.match(/Chunks - (\d+) out of (\d+) chunks processed/);
    if (chunkMatch) {
      const currentChunk = parseInt(chunkMatch[1]);
      const totalChunks = parseInt(chunkMatch[2]);
      return {
        deviceId: '',
        deviceName: '',
        status: 'in_progress',
        message: message,
        currentChunk,
        totalChunks,
        progress: Math.round((currentChunk / totalChunks) * 100)
      };
    }

    // Check for other progress messages
    if (message.includes('In Progress') || message.includes('Processing')) {
      return {
        deviceId: '',
        deviceName: '',
        status: 'in_progress',
        message: message
      };
    }

    return null;
  }

  handlePollingUpdate(deviceId: string, deviceName: string, data: any) {
    const weldParamsData = data.weld_parameter_logs?.data;
    if (!weldParamsData) return;

    // Handle JSON response format like {"data": "Weld Parameter has been downloaded successfully"}
    let actualMessage = weldParamsData;
    if (typeof weldParamsData === 'object' && weldParamsData.data) {
      actualMessage = weldParamsData.data;
    }

    // Find the active download for this device
    const activeDownloads = this.downloadProgressContext?.activeDownloads || [];
    const deviceDownload = activeDownloads.find((download: any) => {
      return download.deviceId === deviceId && download.deviceName === deviceName;
    });

    if (!deviceDownload) {
      // If no active download found and context is not set, just return
      if (!this.downloadProgressContext) {
        return;
      }
      // If no active download found, create a new one
      const downloadId = this.startDownload(deviceId, deviceName);
      this.handlePollingUpdate(deviceId, deviceName, data);
      return;
    }

    const downloadId = deviceDownload.id;

    // Handle different weld parameter states
    if (actualMessage === 'Weld Parameter has been downloaded successfully') {
      this.completeDownload(downloadId);
    } else if (actualMessage.includes('Weld Parameter Download is initiated, please check back later')) {
      this.updateDownloadStatus(downloadId, {
        deviceId,
        deviceName,
        status: 'in_progress',
        message: actualMessage,
        progress: 0
      });
    } else if (actualMessage.includes('out of') && actualMessage.includes('chunks processed')) {
      // Parse chunk progress - handles both "Chunks - 5 out of 40 chunks processed" and "5 out of 40 chunks processed"
      const chunkMatch = actualMessage.match(/(\d+)\s+out\s+of\s+(\d+)\s+chunks\s+processed/);
      if (chunkMatch) {
        const currentChunk = parseInt(chunkMatch[1]);
        const totalChunks = parseInt(chunkMatch[2]);
        const progress = Math.round((currentChunk / totalChunks) * 100);
        
        // If all chunks are processed, mark as completed
        if (currentChunk >= totalChunks) {
          this.completeDownload(downloadId);
        } else {
          this.updateDownloadStatus(downloadId, {
            deviceId,
            deviceName,
            status: 'in_progress',
            message: actualMessage,
            progress: progress,
            currentChunk,
            totalChunks
          });
        }
      } else {
        this.updateDownloadStatus(downloadId, {
          deviceId,
          deviceName,
          status: 'in_progress',
          message: actualMessage
        });
      }
    } else if (actualMessage.includes('In Progress') || actualMessage.includes('Processing')) {
      this.updateDownloadStatus(downloadId, {
        deviceId,
        deviceName,
        status: 'in_progress',
        message: actualMessage
      });
    }
  }
}

export const weldParameterDownloadService = WeldParameterDownloadService.getInstance();
